import { useFormat } from '@shared/hooks/useFormat';
import { GlobalMessages } from '@shared/localization/messages.ts';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import type { Font as ExcelJSFont } from 'exceljs';
import escape from 'lodash/escape';
import { useCallback } from 'react';

import { useTenantQueryActions } from '@/api/hooks/tenantQueryActions';
import { useCsvExport } from '@/components/organism/DataTable/hooks/exports/useCsvExport';
import { usePdfExport } from '@/components/organism/DataTable/hooks/exports/usePdfExport';
import { eExportFormat } from '@/components/organism/DataTable/model';
import { useMessages } from '@/hooks/messages';
import { useHasGlobalPermission } from '@/hooks/useHasGlobalPermission';
import { sanitizeFilename } from '@/utils/fileSystem';
import { htmlToPlainText } from '@/utils/html';

import type { ExportProps, ExportTable, HeaderFooterDataPromise } from './model';
import { useFormatCell } from './useFormatCell';

const maxExcelSheetNameLength = 31;

// Open a print window without document.write (Aikido SAST: XSS via document.write).
// The HTML is served to the new window through a Blob URL instead of being written
// into the document stream, which avoids the document.write sink entirely.
function openPrintWindow(html: string): void {
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const winPrint = window.open(
    url,
    '',
    'left=0,top=0,width=1000,height=600,toolbar=0,scrollbars=0,status=0',
  );
  if (!winPrint) {
    URL.revokeObjectURL(url);
    return;
  }
  const triggerPrint = () => {
    winPrint.focus();
    winPrint.print();
    URL.revokeObjectURL(url);
  };
  if (winPrint.document.readyState === 'complete') {
    triggerPrint();
  } else {
    winPrint.addEventListener('load', triggerPrint, { once: true });
  }
}

interface ExportStyleConfig {
  tableHeaderFontColor?: string | null;
  tableHeaderBackgroundColor?: string | null;
  tableHeaderFontFamily?: string | null;
  tableHeaderBold?: boolean | null;
  tableHeaderItalic?: boolean | null;
  tableRowFontColor?: string | null;
  tableRowBackgroundColor?: string | null;
  tableRowFontFamily?: string | null;
  tableRowBold?: boolean | null;
  tableRowItalic?: boolean | null;
  tableSummaryFontColor?: string | null;
  tableSummaryBackgroundColor?: string | null;
  tableSummaryFontFamily?: string | null;
  tableSummaryBold?: boolean | null;
  tableSummaryItalic?: boolean | null;
}

export const useExport = ({ orientation, fileName }: ExportProps) => {
  const messages = useMessages(GlobalMessages);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const navigator = window.navigator as any;
  const { formatDateTime } = useFormat(messages.yes(), messages.no());
  const formatCell = useFormatCell();

  // EWM-667: export-config is admin-gated (CAN_MANAGE_TENANT_SETTINGS); Estimators always 403.
  // Gate the query off the permission so it never fires for non-admins (they already received
  // no config) and the global AxiosErrorHandler doesn't surface a spurious "not authorized" toast.
  const canManageTenantSettings = useHasGlobalPermission('CAN_MANAGE_TENANT_SETTINGS');

  const { useGetTenantExportConfig } = useTenantQueryActions();
  const { data: exportConfig } = useGetTenantExportConfig({
    enabled: canManageTenantSettings === true,
  }) as {
    data: ExportStyleConfig | undefined;
  };

  const csvExporter = useCsvExport();
  const { exportPdf: pdfExporter } = usePdfExport({ orientation });

  const exportToExcel = useCallback(
    async (tables: ExportTable[], styleConfig?: ExportStyleConfig) => {
      // EWM-1048: exceljs is ~899 kB minified - 62% of this chunk on its own. Imported here
      // rather than at module scope so it is fetched only when a user actually exports to Excel.
      const { default: ExcelJS } = await import('exceljs');
      const workbook = new ExcelJS.Workbook();

      for (let tablesIndex = 0; tablesIndex < tables.length; tablesIndex++) {
        const table = tables[tablesIndex];
        const worksheet = workbook.addWorksheet(
          (table.title ?? `Sheet ${tablesIndex + 1}`).slice(0, maxExcelSheetNameLength - 3) +
            (table.title && table.title.length > maxExcelSheetNameLength ? '...' : ''),
        );

        const headerRow = table.columns.map((x) => x.headerLabel);
        worksheet.addRow(headerRow);

        table.rows.forEach((item) => {
          const row = table.columns.map((col) => {
            const cellData = item[col.accessor];
            const dataToAdd = dayjs.isDayjs(cellData)
              ? formatDateTime(cellData)
              : col.isHtml && typeof cellData === 'string'
                ? htmlToPlainText(cellData)
                : cellData;

            return dataToAdd || '';
          });

          worksheet.addRow(row);
        });

        const columnWidths = table.columns.map((_, colIndex) => {
          let maxLength = headerRow[colIndex]?.toString().length || 0;
          table.rows.forEach((item) => {
            const cellData = item[table.columns[colIndex].accessor];
            const dataToAdd = dayjs.isDayjs(cellData)
              ? formatDateTime(cellData)
              : table.columns[colIndex].isHtml && typeof cellData === 'string'
                ? htmlToPlainText(cellData)
                : cellData;
            const cellLength = (dataToAdd || '').toString().length;
            if (cellLength > maxLength) {
              maxLength = cellLength;
            }
          });
          return maxLength + 2;
        });

        worksheet.columns = columnWidths.map((width) => ({ width }));

        if (styleConfig) {
          const totalRows = worksheet.rowCount;
          const totalCols = table.columns.length;

          for (let rowIndex = 1; rowIndex <= totalRows; rowIndex++) {
            const row = worksheet.getRow(rowIndex);
            const isHeaderRow = rowIndex === 1;
            const isSummaryRow = rowIndex === totalRows;

            let cellStyleConfig;
            if (isHeaderRow) {
              cellStyleConfig = {
                bgColor: styleConfig.tableHeaderBackgroundColor,
                fontColor: styleConfig.tableHeaderFontColor,
                fontFamily: styleConfig.tableHeaderFontFamily,
                bold: styleConfig.tableHeaderBold,
                italic: styleConfig.tableHeaderItalic,
              };
            } else if (isSummaryRow) {
              cellStyleConfig = {
                bgColor: styleConfig.tableSummaryBackgroundColor,
                fontColor: styleConfig.tableSummaryFontColor,
                fontFamily: styleConfig.tableSummaryFontFamily,
                bold: styleConfig.tableSummaryBold,
                italic: styleConfig.tableSummaryItalic,
              };
            } else {
              cellStyleConfig = {
                bgColor: styleConfig.tableRowBackgroundColor,
                fontColor: styleConfig.tableRowFontColor,
                fontFamily: styleConfig.tableRowFontFamily,
                bold: styleConfig.tableRowBold,
                italic: styleConfig.tableRowItalic,
              };
            }

            for (let colIndex = 1; colIndex <= totalCols; colIndex++) {
              const cell = row.getCell(colIndex);

              if (cellStyleConfig.bgColor) {
                const hex = cellStyleConfig.bgColor.replace('#', '');
                cell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: { argb: `FF${hex}` },
                };
              }

              // Create font style object - start with existing font to preserve any defaults
              const fontStyle: Partial<ExcelJSFont> = cell.font ? { ...cell.font } : {};

              if (cellStyleConfig.fontFamily) {
                fontStyle.name = cellStyleConfig.fontFamily;
              }
              if (cellStyleConfig.fontColor) {
                const hex = cellStyleConfig.fontColor.replace('#', '');
                fontStyle.color = { argb: `FF${hex}` };
              }
              // Explicitly set bold - this ensures it applies to all cells including dates
              if (cellStyleConfig.bold !== undefined && cellStyleConfig.bold !== null) {
                fontStyle.bold = cellStyleConfig.bold;
              }
              if (cellStyleConfig.italic !== undefined && cellStyleConfig.italic !== null) {
                fontStyle.italic = cellStyleConfig.italic;
              }
              // Always apply font style to ensure consistency across all cells
              cell.font = fontStyle;
            }
          }
        }
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const excelBlob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = sanitizeFilename(`${fileName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [fileName, formatDateTime],
  );

  const styleConfig = exportConfig;

  const exportToPrint = useCallback(
    (tables: ExportTable[]) => {
      let result = `
        <style>
          .reset-inner-html-style {
            ol {
              list-style: auto;
              margin: 14px 0 14px 0;
              padding-left: 40px;

              /* quill does it this way: https://github.com/slab/quill/issues/3014#issuecomment-2492501253 */
              li[data-list='bullet'] {
                list-style: disc;
              }
            }
          }
        </style>`;

      tables.forEach((table) => {
        result += '<div style="page-break-inside: avoid;page-break-after: always;">';
        if (table.title) {
          result += `<h1>${table.title}</h1>`;
        }

        result +=
          '<table style="width: 100%;"><thead><tr style="color: #515365; background: #eff5ff; -webkit-print-color-adjust: exact; print-color-adjust: exact;">';

        table.columns.forEach((column) => {
          result += `<th>${column.headerLabel}</th>`;
        });

        result += '</tr></thead><tbody>';

        table.rows.forEach((row) => {
          result += '<tr>';
          table.columns.forEach(({ accessor, isHtml }) => {
            const val = row[accessor] === null || row[accessor] === undefined ? '' : row[accessor];
            if (isHtml) {
              if (typeof val !== 'string') {
                throw new Error('Html column must be of type string');
              }
              result += `<td class="reset-inner-html-style">${DOMPurify.sanitize(val)}</td>`;
            } else {
              const htmlEscapedFormattedValue = escape(formatCell(val) as string | undefined);
              result += `<td>${htmlEscapedFormattedValue}</td>`;
            }
          });
          result += '</tr>';
        });

        result += `
      <style>
        body {font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace; color: #495057;}        
        table { border-collapse: collapse; border-spacing: 0; }
        th, td {font-size: 12px; text-align: left; padding: 4px; border: 1px solid #c3c3c3;}
        th {padding: 8px 4px;}
        tr:nth-child(2n-1) {background: #f7f7f7;}
      </style>
    `;
        result += '</tbody></table>';
        result += '</div>';
      });

      openPrintWindow(`<title>Print</title>${result}`);
    },
    [formatCell],
  );

  const exportToJson = useCallback(
    (tables: ExportTable[]) => {
      const json = JSON.stringify(
        tables.map((table) => ({
          title: table.title,
          rows: table.rows,
        })),
        null,
        2,
      );

      if (!navigator.msSaveOrOpenBlob) {
        const data = 'data:application/json;charset=utf-8,' + encodeURIComponent(json);
        const link = document.createElement('a');
        link.setAttribute('href', data);
        link.setAttribute('download', sanitizeFilename(`${fileName}.json`));
        link.click();
      } else {
        const blob = new Blob([json], { type: 'application/json' });
        if (navigator.msSaveOrOpenBlob) {
          navigator.msSaveBlob(blob, sanitizeFilename(`${fileName}.json`));
        }
      }
    },
    [navigator, fileName],
  );

  return useCallback(
    (
      format: eExportFormat,
      tables: ExportTable[],
      headerFooterDataPromise: HeaderFooterDataPromise,
    ) => {
      switch (format) {
        case eExportFormat.CSV:
          void csvExporter(fileName, tables);
          break;
        case eExportFormat.PRINT:
          exportToPrint(tables);
          break;
        case eExportFormat.JSON:
          exportToJson(tables);
          break;
        case eExportFormat.PDF:
          void pdfExporter(fileName, tables, headerFooterDataPromise, styleConfig);
          break;
        case eExportFormat.EXCEL:
          void exportToExcel(tables, styleConfig);
          break;
      }
    },
    [csvExporter, exportToExcel, exportToJson, exportToPrint, fileName, pdfExporter, styleConfig],
  );
};
